import { orpc } from '@client-info/utils/orpc';
import type { AreYouOk } from '@server/are-you-ok/are-you-ok.dto';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { errorMessageAtom } from '../../ErrorSnackbar';

export const queryKey = ['areYouOk'];

export const useGetAreYouOkById = (id: string) =>
  useQuery(
    orpc.areYouOk.byId.queryOptions({
      queryKey: [...queryKey, id],
      input: { id },
    }),
  );

export const useMutationAreYouOkState = (id: string) => {
  const queryClient = useQueryClient();
  const setErrorMessage = useSetAtom(errorMessageAtom);
  return useMutation({
    ...orpc.areYouOk.setStatus.mutationOptions({
      onSuccess: (mutation) =>
        queryClient.setQueryData<AreYouOk>(
          [...queryKey, id],
          (prevState) => prevState && { ...prevState, ...mutation },
        ),
      onError: () => {
        setErrorMessage('Failed to update are you ok status');
        queryClient.invalidateQueries({ queryKey: [...queryKey, id] });
      },
    }),
    mutationKey: [...queryKey, id],
  });
};

export const useSetPendingResponse = (id: string) => {
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const queryClient = useQueryClient();
  return useMutation(
    orpc.areYouOk.setPendingResponse.mutationOptions({
      onSuccess: (pendingResponse) =>
        queryClient.setQueryData<AreYouOk>(
          [...queryKey, id],
          (prevState) =>
            prevState && {
              ...prevState,
              pending: pendingResponse,
            },
        ),
      onError: () => {
        setErrorMessage('Failed to initiate are you ok status');
      },
    }),
  );
};

export const useCancelPendingResponse = (id: string) => {
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const queryClient = useQueryClient();
  return useMutation(
    orpc.areYouOk.deletePendingResponse.mutationOptions({
      onSuccess: () =>
        queryClient.setQueryData<AreYouOk>(
          [...queryKey, id],
          (prevState) =>
            prevState && {
              ...prevState,
              pending: undefined,
            },
        ),
      onError: () => {
        setErrorMessage('Failed to cancel are you ok status');
      },
    }),
  );
};
